import axios from 'axios';
import { API_CONFIG } from './apiConfig';

const MAX_RECORDS = 200;
const telemetryState = {
  installed: false,
  context: { route: getRouteCategory(), tab: 'unknown' },
};

const apiOrigins = new Set([
  API_CONFIG.API_BASE_URL,
  API_CONFIG.SPECIAL_API_BASE_URL,
  API_CONFIG.MAPS_BASE_URL,
].map((value) => {
  try {
    return new URL(value).origin;
  } catch {
    return null;
  }
}).filter(Boolean));

function getRouteCategory() {
  if (typeof window === 'undefined') return 'server';
  const pathname = window.location.pathname || '';
  if (pathname.startsWith('/f/')) return 'shared-file';
  if (pathname.endsWith('/guest.html')) return 'guest-entry';
  if (pathname.split('/').filter(Boolean).length === 1) return 'guest-tour';
  return 'application';
}

function normalizeRequestUrl(value) {
  if (!value || typeof window === 'undefined') return null;
  try {
    const url = new URL(value, window.location.origin);
    return apiOrigins.has(url.origin) ? url : null;
  } catch {
    return null;
  }
}

function latestResourceTiming(url, startedAt) {
  if (typeof performance === 'undefined' || !url) return null;
  const endpoint = `${url.origin}${url.pathname}`;
  const entries = performance.getEntriesByType('resource')
    .filter((entry) => entry.name.startsWith(endpoint));
  return entries
    .filter((entry) => entry.startTime >= startedAt - 10)
    .slice(-1)[0] || null;
}

function pushRecord(record) {
  if (typeof window === 'undefined') return;
  const records = window.__qrmapsApiTelemetry || [];
  records.push(record);
  if (records.length > MAX_RECORDS) records.splice(0, records.length - MAX_RECORDS);
  window.__qrmapsApiTelemetry = records;
  window.dispatchEvent(new CustomEvent('qrmaps:api-telemetry', { detail: record }));

  if (import.meta.env.DEV && import.meta.env.VITE_API_TELEMETRY_DEBUG === 'true') {
    console.debug('[api telemetry]', record);
  }
}

function completeRecord(config, response = null, error = null) {
  const metadata = config?.__apiTelemetry;
  if (!metadata || metadata.skip) return;

  const finishedAt = typeof performance !== 'undefined' ? performance.now() : Date.now();
  const resource = latestResourceTiming(metadata.url, metadata.startedAt);
  const headers = response?.headers || error?.response?.headers || {};
  const status = response?.status || error?.response?.status || null;

  pushRecord({
    event: 'client_api_performance',
    endpoint: metadata.url.pathname,
    method: String(config.method || 'get').toUpperCase(),
    route: metadata.route,
    tab: metadata.tab,
    status_code: status,
    duration_ms: Number((finishedAt - metadata.startedAt).toFixed(2)),
    transfer_size: resource?.transferSize ?? null,
    encoded_body_size: resource?.encodedBodySize ?? null,
    decoded_body_size: resource?.decodedBodySize ?? null,
    server_timing: headers['server-timing'] || null,
    request_id: headers['x-request-id'] || null,
    cache_status: headers['x-api-cache-status'] || null,
    failed: Boolean(error),
  });
}

export function setApiTelemetryContext({ route, tab } = {}) {
  telemetryState.context = {
    route: route || getRouteCategory(),
    tab: tab || 'unknown',
  };
}

export function installApiTelemetry() {
  if (telemetryState.installed || typeof window === 'undefined') return;
  telemetryState.installed = true;

  axios.interceptors.request.use((config) => {
    const url = normalizeRequestUrl(config.url);
    if (!url || config.__apiTelemetry?.skip) return config;

    const context = telemetryState.context;
    config.__apiTelemetry = {
      url,
      route: context.route,
      tab: context.tab,
      startedAt: performance.now(),
    };
    return config;
  });

  axios.interceptors.response.use(
    (response) => {
      completeRecord(response.config, response);
      return response;
    },
    (error) => {
      completeRecord(error.config, null, error);
      return Promise.reject(error);
    }
  );
}
